import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { withRouter } from 'react-router-dom';
import { observer, inject } from 'mobx-react';
import SubscriptionService from '../../api/SubscriptionService';
import ApiStore from '../../api/ApiStore';
import { ArrowLeft } from '@phosphor-icons/react';

const SubscriptionSetup = ({ history, location, store }) => {
  const stripe = useStripe();
  const elements = useElements();

  // Get organization code from session storage
  // This could be set during login or when renewing a canceled subscription
  const orgCode = sessionStorage.getItem('pendingOrgCode');

  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [error, setError] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);

  // Load organization details on component mount - only once
  useEffect(() => {
    const getOrganizationDetails = async () => {
      try {
        // First check if we have a code from session storage
        if (orgCode) {
          // Try to get organization details using the code
          const response =
            await SubscriptionService.getOrganizationByCode(orgCode);

          if (response) {
            // Create organization object with fallbacks for null values
            setOrganization({
              organization_name:
                response.organization_name ||
                store.profile.organization ||
                'Tu organización',
              subscription_amount: response.subscription_amount || 0,
              friendly_code: orgCode,
            });

            // Clear any previous error
            setError(null);
            setShowManualEntry(false);

            console.log(
              `Successfully loaded organization with code: ${orgCode}`
            );
            return;
          }
        }

        // If no org code in session or it failed, try to get directly from current user's organization
        try {
          const orgData =
            await SubscriptionService.getCurrentUserOrganizationCode();
          console.log('Organization code API response:', orgData);

          // Check if we have the organization_code (friendly_code) field, even if other fields are null
          if (orgData && orgData.organization_code) {
            // Store the code for future reference
            sessionStorage.setItem('pendingOrgCode', orgData.organization_code);

            // Set organization with fallbacks for null values
            setOrganization({
              organization_name:
                orgData.organization_name ||
                store.profile.organization ||
                'Tu organización',
              subscription_amount: orgData.subscription_amount || 0,
              friendly_code: orgData.organization_code,
            });

            // Clear any previous error
            setError(null);
            setShowManualEntry(false);

            console.log(
              `Successfully loaded organization code: ${orgData.organization_code}`
            );
            return;
          }
        } catch (error) {
          console.error(
            'Failed to get organization code for current user:',
            error
          );
        }

        // If we still don't have it, try one last fallback from stored org info
        const pendingOrgInfo = sessionStorage.getItem('pendingOrgInfo');
        if (pendingOrgInfo) {
          try {
            const orgInfo = JSON.parse(pendingOrgInfo);
            // We have org info but not the code - this is a partial solution
            // We'll show the manual entry with the organization name pre-filled
            setShowManualEntry(true);
            setError(`Por favor, introduzca el código para ${orgInfo.name}`);
            return;
          } catch (e) {
            console.error('Error parsing pending org info', e);
          }
        }

        // If all auto-resolution attempts fail, show manual entry
        setShowManualEntry(true);
        setError('Por favor, introduzca el código de su autoescuela');
      } catch (err) {
        console.error('Error loading organization:', err);
        setShowManualEntry(true);
        setError('No se pudo cargar la información de la autoescuela');
      } finally {
        setLoading(false);
      }
    };

    getOrganizationDetails();
  }, [orgCode]);

  // Handle manual code submission
  const handleManualCodeSubmit = async e => {
    e.preventDefault();

    if (!manualCode.trim()) {
      setError('Por favor, introduzca el código de la autoescuela');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await SubscriptionService.getOrganizationByCode(
        manualCode.trim()
      );

      if (response && response.organization_name) {
        setOrganization({
          organization_name: response.organization_name,
          subscription_amount: response.subscription_amount,
          friendly_code: manualCode.trim(),
        });
        setShowManualEntry(false);

        // Save the code for future reference
        sessionStorage.setItem('pendingOrgCode', manualCode.trim());
      } else {
        setError('Código de autoescuela no válido');
      }
    } catch (err) {
      setError('Error al validar el código. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Create subscription with payment method
  const handleSubmit = async event => {
    event.preventDefault();

    // Enhanced error handling for Stripe initialization issues
    if (!stripe || !elements) {
      console.error('Stripe not initialized properly:', {
        stripe: !!stripe,
        elements: !!elements,
      });
      setError(
        'Error al inicializar el procesador de pagos. Por favor, recargue la página.'
      );
      return;
    }

    if (!organization?.friendly_code) {
      console.error('Missing organization code:', organization);
      setError(
        'Falta el código de organización. Por favor, contacte con soporte.'
      );
      return;
    }

    setProcessingPayment(true);
    setError(null);

    try {
      // Create payment method using Stripe
      const cardElement = elements.getElement(CardElement);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        throw new Error(error.message);
      }

      // Create subscription with our API
      const subscriptionResponse = await SubscriptionService.createSubscription(
        organization.friendly_code,
        paymentMethod.id
      );

      // Check if 3D Secure authentication is required
      if (subscriptionResponse.client_secret) {
        const { error: confirmError } = await stripe.confirmCardPayment(
          subscriptionResponse.client_secret
        );

        if (confirmError) {
          throw new Error(confirmError.message);
        }
      }

      // Success - clean up all subscription-related session storage
      sessionStorage.removeItem('pendingOrgCode');
      sessionStorage.removeItem('pendingSetupUser');
      sessionStorage.removeItem('pendingSubscription');
      sessionStorage.removeItem('pendingOrgInfo');
      sessionStorage.removeItem('canceledSubscription');

      // Update store state
      if (store.requiresSubscriptionSetup) {
        store.requiresSubscriptionSetup = false;
      }

      // Redirect to success page
      history.push('/student/subscriptions/success', {
        organizationName: organization.organization_name,
        subscriptionId: subscriptionResponse.subscription_id,
        paymentSuccess: true,
      });
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Error al procesar el pago');
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <div className="w-full p-8 overflow-y-auto flex items-center justify-center flex-1">
        <div className="w-full md:max-w-md 2xl:max-w-xl">
          <div className="bg-white rounded-xl shadow-xl transform transition-all shadow-md hover:shadow-2xl focus:shadow-2xl">
            <div className="px-4 py-4 md:px-12 md:py-12">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-16 w-16 mb-6">
                <img
                  src="/logo_dark.jpg"
                  alt="Logo"
                  className="h-16 w-16 rounded-full object-cover"
                />
              </div>

              <button
                onClick={() => history.goBack()}
                className="mb-4 flex items-center text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft size={20} className="mr-1" />
                <span>Volver</span>
              </button>

              <div className="text-3xl font-medium text-gray-900 mb-4">
                Completa tu suscripción
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-md mb-6">
                  {error}
                </div>
              )}

              {/* Manual entry form */}
              {showManualEntry && (
                <div className="mb-6">
                  <div className="text-gray-700 mb-3">
                    Por favor, introduzca el código de organización
                    proporcionado por su autoescuela:
                  </div>

                  <form onSubmit={handleManualCodeSubmit}>
                    <div className="mb-4">
                      <input
                        type="text"
                        value={manualCode}
                        onChange={e => setManualCode(e.target.value)}
                        placeholder="Código de Organización"
                        className="rounded-md text-lg px-4 py-2 border border-gray-300 w-full"
                      />
                    </div>

                    <button
                      type="submit"
                      className="hover:bg-gray-600 font-medium rounded-lg text-lg px-4 py-2 bg-gray-500 text-white w-full"
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                          <span>Verificando...</span>
                        </div>
                      ) : (
                        'Continuar'
                      )}
                    </button>
                  </form>
                </div>
              )}

              {/* Subscription form */}
              {organization && !showManualEntry && (
                <>
                  <div className="text-gray-600 mb-6">
                    {organization.organization_name}
                    {organization.subscription_amount && (
                      <span className="font-semibold ml-1">
                        ({organization.subscription_amount}€/mes)
                      </span>
                    )}
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className="mb-8 p-3 border border-gray-200 rounded-md bg-gray-50">
                      <div className="font-medium text-lg mb-2">
                        Detalles de la suscripción
                      </div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Tarifa mensual:</span>
                        <span className="font-semibold">
                          {organization.subscription_amount?.toFixed(2)}€
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Facturación:</span>
                        <span>Mensual (renovación automática)</span>
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Método de pago
                      </label>
                      <div className="p-4 border border-gray-300 rounded-md">
                        <CardElement
                          options={{
                            style: {
                              base: {
                                fontSize: '16px',
                                color: '#424770',
                                '::placeholder': {
                                  color: '#aab7c4',
                                },
                              },
                              invalid: {
                                color: '#9e2146',
                              },
                            },
                          }}
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="hover:bg-gray-600 font-medium rounded-lg text-lg px-4 py-2 bg-gray-500 text-white w-full"
                      disabled={processingPayment || !stripe}
                    >
                      {processingPayment ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                          <span>Procesando...</span>
                        </div>
                      ) : (
                        `Suscribirse - ${organization.subscription_amount?.toFixed(2)}€/mes`
                      )}
                    </button>

                    <p className="text-xs text-gray-500 mt-3 text-center">
                      Al suscribirte, autorizas a{' '}
                      {organization.organization_name} a cargar tu tarjeta
                      mensualmente hasta que canceles.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default inject('store')(observer(withRouter(SubscriptionSetup)));
